import { ref, type Ref } from 'vue';
import { http } from '@/interceptors/axiosRequestInterceptor';

/**
 * On-demand translation of order correspondence (AB#21162).
 *
 * The state here is per instance, so a component owns the translation of the one
 * message it renders rather than reaching into shared state keyed by id.
 *
 * The original is never replaced. `translated` is held alongside the message the
 * component already has, and `showingTranslation` decides which one is on screen —
 * so "Show original" is a local toggle with nothing to re-fetch, and nothing in the
 * app ever loses the text the sender actually wrote. That matters beyond
 * convenience: an agent quoting a machine translation back to a customer as their
 * own words is the failure this design is meant to make impossible.
 */
export interface TranslationPayload {
  translation: string | null;
  source_language: string | null;
  cached?: boolean;
  machine_translated?: boolean;
}

export type TranslatableType = 'order_comment';

export interface TranslationState {
  translated: Ref<string | null>;
  sourceLanguage: Ref<string | null>;
  showingTranslation: Ref<boolean>;
  isTranslating: Ref<boolean>;
  error: Ref<string | null>;
  translate: () => Promise<void>;
  showOriginal: () => void;
}

export const useTranslation = (type: TranslatableType, id: number): TranslationState => {
  const translated = ref<string | null>(null);
  const sourceLanguage = ref<string | null>(null);
  const showingTranslation = ref(false);
  const isTranslating = ref(false);
  const error = ref<string | null>(null);

  const showOriginal = (): void => {
    showingTranslation.value = false;
  };

  const translate = async (): Promise<void> => {
    // Already have it — switching back and forth is free and costs no call.
    if (translated.value !== null) {
      showingTranslation.value = true;
      error.value = null;
      return;
    }

    isTranslating.value = true;
    error.value = null;

    try {
      const { data } = await http.post('translate-message', { type, id });
      const result = (data?.result ?? {}) as TranslationPayload;

      if (data?.success !== true || !result.translation) {
        // The backend answers a provider failure with 200 + success:false and a
        // message written to be shown verbatim — the request was fine, the
        // upstream was not. Anything else, fall back to our own wording rather
        // than showing the agent a blank.
        error.value = data?.message || 'Could not translate this right now.';
        return;
      }

      translated.value = result.translation;
      sourceLanguage.value = result.source_language;
      showingTranslation.value = true;
    } catch {
      // Deliberately not handleResponseException: a failed translation is not a
      // page-level error, and a toast over the order screen would overstate it.
      // The message belongs next to the comment it failed on.
      error.value = 'Could not translate this right now.';
    } finally {
      isTranslating.value = false;
    }
  };

  return {
    translated,
    sourceLanguage,
    showingTranslation,
    isTranslating,
    error,
    translate,
    showOriginal,
  };
};

/**
 * Language names for the "translated from X" label.
 *
 * Intl.DisplayNames does the work and covers every code the provider can report;
 * this only handles the case where it cannot (an unknown or malformed code), where
 * showing the raw code is better than showing nothing.
 */
export const languageName = (code: string | null): string => {
  if (!code) return '';
  try {
    return new Intl.DisplayNames(['en'], { type: 'language' }).of(code) || code;
  } catch {
    return code;
  }
};
